import { useState } from 'react';
import { pilotFileExtensions } from '@common/utils/fileExtensions';
import type { IMedication } from '../../../../../interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '../../../interfaces/';
import { getServiceLabel } from '../../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../../common/enums/filesUrlBase';
import { PractitionerRoleInRequest } from '../../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../../translate';
import { useEmailAddressControlComponent } from '../../../../../../utils/hooks/useEmailAddressControlComponent';
import { useEpisodeContext } from '../../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../../utils/showToast';
import { CancelButton } from '../../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider, HeaderDivider, InputTextArea } from '../../../../../elements/common';
import { FormLabel } from '../../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../../episodes/common';
import { ProfessionalSummaryData } from '../../../../professionalSummaryData/ProfessionalSummaryData';
import { AssetDropzoneWithActions } from '../../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../../utils/v1/CareProviderSelector';
import { MedicationsAddEdit } from '../../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../../utils/v1/ServiceOrganizationProvider';

export function PilotExaminationV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const { errors, request, handleChangeCustomControl, validateOneCustomControl } = props;
    const { patient } = useEpisodeContext();

    const { EmailAddressControlProps, EmailAddressControl, validateEmailAddress } = useEmailAddressControlComponent({
        emailAddress: request.customControlData.emailadres
            ? request.customControlData.emailadres
            : patient.emailAddress,
        handleChangeCustomControl,
        validateOneCustomControl,
    });

    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

    return (
        <Form onSubmit={() => null}>
            <NewOrEditRequestProcessContainer
                isExaminationRequest
                isFirstItem={props.isFirstItem}
                itemState={props.itemState}
                process={props.process}
                service={props.service}
                title={getServiceLabel(props.service.code, 'request', props.service.name)}
            >
                {addEditForm()}
            </NewOrEditRequestProcessContainer>

            <EmailAddressControl
                {...EmailAddressControlProps}
                error={errors.get('customControlData.emailadres')}
            />

            <ProcessContainer noBorderTop>
                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!request.id}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </ProcessContainer>
        </Form>
    );

    function addEditForm() {
        return (
            <>
                <CareProviderSelector
                    {...props}
                    entity={request}
                />

                <ServiceOrganizationProvider
                    errors={errors}
                    initialOrganizationId={request.selectedProcedureProviderOrganizationId}
                    initialPractitionerId={request.selectedProcedureProviderPractitionerId}
                    isExaminationRequest
                    onChange={async (organizationId, practitionerId) => {
                        await props.handleChange('selectedProcedureProviderOrganizationId', organizationId);
                        await props.handleChange('selectedProcedureProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await props.validateOne('selectedProcedureProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.biometrician}
                    serviceProviderServiceId={props.service.id}
                />

                <FormDivider />

                <HeaderDivider text={translateKey('procedure.procedure')} />

                <AssetDropzoneWithActions
                    acceptedFiles={pilotFileExtensions}
                    assets={request.assets}
                    assetsBaseUrl={FilesUrlBase.requests}
                    onChange={async (assets) => await props.handleChange('assets', assets)}
                    onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                    processActivityId={request.id}
                    service={props.service}
                    showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                    updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={request.id}
                    errors={props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={request.patientUsesMedication}
                    validateMedications={async () => await props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={request.id}
                    errors={props.errors}
                    handleChangeAllergies={async (allergies) => await props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => props.validateOne('requestEpisodesAndJournal')}
                />

                <HorizontalFormGroup hasErrors={props.errors.has('customControlData.indicatie')}>
                    <FormLabel>{translateKey('request.indication')}</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            error={props.errors.get('customControlData.indicatie')}
                            onChange={async (value) => await handleChangeCustomControl({ indicatie: value })}
                            onDoValidate={async () => await validateOneCustomControl('indicatie')}
                            placeholder={translateKey('request.indication')}
                            value={request.customControlData.indicatie}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={props.errors.has('customControlData.toelichting')}>
                    <FormLabel>{translateKey('common.remarks')}</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            error={props.errors.get('customControlData.toelichting')}
                            onChange={async (value) => await handleChangeCustomControl({ toelichting: value })}
                            onDoValidate={async () => await validateOneCustomControl('toelichting')}
                            placeholder={translateKey('common.remarks')}
                            value={request.customControlData.toelichting}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            </>
        );
    }

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        const isValidatedEmailAddress = validateEmailAddress();
        if (!isValidatedEmailAddress) {
            setButtonDisabled(false);

            return;
        }
        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        await props.onSave(requestId);
    }
}

export function pilotExaminationV1RequestInitializeCustomControlData() {
    return {
        emailadres: null,
        indicatie: null,
        toelichting: null,
    };
}
